/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import * as Msdyn365 from '@msdyn365-commerce/core';
import { getCourseDataAsync } from './DataActionExtension.g';
import { ICourseResponseEntity } from './DataServiceEntities.g';
import { buildCacheKey, QueryResultSettingsProxy } from '@msdyn365-commerce-modules/retail-actions';
import { IQueryResultSettings } from '@msdyn365-commerce/retail-proxy';

/**
 * GetCourseListInput Input Action
 */

export class GetCourseListInput implements Msdyn365.IActionInput {
    public queryResultSettings: IQueryResultSettings;
    public apiSettings: Msdyn365.ICommerceApiSettings;

    constructor(queryResultSettings: IQueryResultSettings, apiSettings: Msdyn365.ICommerceApiSettings) {
        this.queryResultSettings = queryResultSettings;
        this.apiSettings = apiSettings;
    }

    public getCacheKey = () => {
        const baseCacheKey = 'CourseList';
        const queryResultSettings = this.queryResultSettings.Paging ? this.queryResultSettings.Paging : null;
        if (queryResultSettings) {
            return buildCacheKey(
                `${baseCacheKey}-${queryResultSettings.Top ? +'-' + queryResultSettings.Top : ''}${
                    queryResultSettings.Skip ? +'-' + queryResultSettings.Skip : ''
                }`,
                this.apiSettings
            );
        } else {
            return buildCacheKey(`${baseCacheKey}`, this.apiSettings);
        }
    };
    public getCacheObjectType = () => 'CourseList';
    public dataCacheType = (): Msdyn365.CacheType => 'request';
}

export interface IGetCourseData {
    courseResponse: ICourseResponseEntity;
}

const createCourseListInput = (args: Msdyn365.ICreateActionContext): Msdyn365.IActionInput => {
    const queryResultSettings = QueryResultSettingsProxy.fromInputData(args).QueryResultSettings;

    return new GetCourseListInput(queryResultSettings, args.requestContext.apiSettings);
};

async function action(input: GetCourseListInput, ctx: Msdyn365.IActionContext): Promise<IGetCourseData> {
    const response = await getCourseDataAsync({ callerContext: ctx, queryResultSettings: input.queryResultSettings });
    if (response && response.length) {
        return { courseResponse: response[0] };
    }
    return { courseResponse: { Courses: [], TotalCount: 0 } };
}

export default Msdyn365.createObservableDataAction({
    action: <Msdyn365.IAction<IGetCourseData>>action,
    id: 'GetCourseList',
    input: createCourseListInput
});
